import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const galleryImages = [
  '/pictures/gallery_image_12_0bbac5a0d5.jpg',
  '/pictures/gallery_image_13_c1cff54d83.jpg',
  '/pictures/gallery_image_14_8e8ce1d53b.jpg',
  '/pictures/gallery_image_2_2aa90a3c8d.jpg',
  '/pictures/gallery_image_3_880e6df637.jpg',
  '/pictures/gallery_image_4_d3e9bea0ec.jpg',
  '/pictures/gallery_image_5_f71d727fde.jpg',
  '/pictures/gallery_image_6_ac68225c36.jpg',
  '/pictures/gallery_image_7_a5de35d063.jpg',
  '/pictures/gallery_image_8_01c3f49512.jpg',
  '/pictures/gallery_image_1_bb7ea841bf.jpg',
  '/pictures/gallery_image_10_d2eb076cb3.jpg',
];

const offices = [
  {
    country: 'Germany',
    role: 'Group headquarter',
    address: 'Willy-Brandt-Platz 19, 38102 Braunschweig, Germany',
    phone: '(+49) 531 2097 8900',
    email: 'info@dynexo.de',
    flag: '🇩🇪',
  },
  {
    country: 'Vietnam',
    role: 'Headquarter',
    address: 'Lầu 1, số 9 đường Trương Quyền, Phường Xuân Hòa, TP Hồ Chí Minh, Việt Nam',
    phone: '(+84) 879 059 915',
    email: 'info@dision.tech',
    flag: '🇻🇳',
  },
];

export default function AboutUs() {
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  const openLightbox = (img) => {
    setLightboxImg(img);
    setLightboxOpen(true);
  };

  return (
    <PageLayout showContact={false}>
      <SEOHead
        title="About Us"
        description="Learn about DISION Tech LLC, a subsidiary of the DISION Group with headquarters in Germany. We offer innovative software development and cybersecurity solutions."
        keywords="about DISION Tech, software company, cybersecurity company, IT services, Ho Chi Minh, Germany"
        url="/about-us"
      />
      
      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .about-content-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .stats-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .offices-grid {
            grid-template-columns: 1fr !important;
          }
          .contact-form-container {
            padding: 24px !important;
          }
          /* Better touch targets - exclude footer */
          main button:not(footer button), 
          main a:not(footer a) {
            min-height: 44px !important;
          }
        }
        @media (max-width: 480px) {
          .gallery-grid {
            grid-template-columns: 1fr !important;
          }
          h1 {
            font-size: clamp(28px, 8vw, 40px) !important;
          }
          h2 {
            font-size: clamp(24px, 6vw, 32px) !important;
          }
        }
      `}</style>
      
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            About us
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            DISION Tech LLC, a subsidiary of the DISION Group with headquarters in Germany, offers a diverse range of innovative products and services for advanced communication and computing needs.
          </p>
        </div>
      </div>

      {/* About content */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div className="about-content-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '17px', color: '#6B7280', lineHeight: 1.85, marginBottom: '24px' }}>
                At DISION, we believe that our technology not only provides solutions but also enhances the core values and frameworks it supports.
              </p>
              <p style={{ fontSize: '17px', color: '#6B7280', lineHeight: 1.85, marginBottom: '24px' }}>
                Our skilled team specializes in software technologies, advanced data communication, and security solutions, ensuring we meet and exceed our clients' high standards.
              </p>
              <p style={{ fontSize: '17px', color: '#6B7280', lineHeight: 1.85 }}>
                From our consulting services to development, complete integration, and operational services, our cutting-edge products and solutions guarantee reliability, integrity, and availability for business requirements.
              </p>

              {/* Stats */}
              <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginTop: '48px' }}>
                {[
                  { num: '50+', label: 'Projects Delivered' },
                  { num: '10+', label: 'Years Experience' },
                  { num: '30+', label: 'Team Members' },
                ].map((stat, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: '24px', borderRadius: '16px', background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: '32px', fontWeight: '800', color: '#0099CC', marginBottom: '6px' }}>{stat.num}</div>
                    <div style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact form */}
            <div>
              <div className="contact-form-container" style={{ background: '#F9FAFB', borderRadius: '20px', padding: '40px', border: '1px solid #E5E7EB' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>We're ready to collaborate with you</h2>
                <p style={{ fontSize: '15px', color: '#6B7280', marginBottom: '28px' }}>Explore our opportunities and contact us today!</p>
                <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} id="contact-form">
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Full name *</label>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="John Doe"
                      required
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #D1D5DB', background: 'white', fontSize: '15px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s ease' }}
                      onFocus={e => e.target.style.borderColor = '#0099CC'}
                      onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="john@company.com"
                      required
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #D1D5DB', background: 'white', fontSize: '15px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s ease' }}
                      onFocus={e => e.target.style.borderColor = '#0099CC'}
                      onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Message</label>
                    <textarea
                      name="message"
                      placeholder="Tell us about your project..."
                      rows={4}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #D1D5DB', background: 'white', fontSize: '15px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.2s ease' }}
                      onFocus={e => e.target.style.borderColor = '#0099CC'}
                      onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                    />
                  </div>
                  <button
                    type="submit"
                    style={{ backgroundColor: '#F97316', color: 'white', padding: '13px 28px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background-color 0.2s ease' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EA580C'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F97316'}
                  >
                    Send message <Send size={16} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map + Addresses */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Map image */}
          <div style={{ borderRadius: '20px', overflow: 'hidden', marginBottom: '56px', lineHeight: 0 }}>
            <img
              src="/pictures/map_57a4730777.png"
              alt="DISION Office locations"
              style={{ width: '100%', height: 'auto', display: 'block' }}
              onError={e => { e.target.src = 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1200&q=80'; }}
            />
          </div>

          {/* Address cards */}
          <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '32px', letterSpacing: '-0.5px' }}>Our address</h2>
          <div className="offices-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {offices.map((office, i) => (
              <div key={i} className="service-card" style={{ background: '#F9FAFB', borderRadius: '16px', padding: '32px', border: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '28px' }}>{office.flag}</span>
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>{office.country}</h3>
                    <span style={{ fontSize: '13px', color: '#0099CC', fontWeight: '600' }}>{office.role}</span>
                  </div>
                </div>
                <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <MapPin size={16} color="#0099CC" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>{office.address}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Phone size={16} color="#0099CC" style={{ flexShrink: 0 }} />
                    <a href={`tel:${office.phone}`} style={{ fontSize: '14px', color: '#374151', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = '#0099CC'} onMouseLeave={e => e.currentTarget.style.color = '#374151'}>{office.phone}</a>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Mail size={16} color="#0099CC" style={{ flexShrink: 0 }} />
                    <a href={`mailto:${office.email}`} style={{ fontSize: '14px', color: '#374151', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = '#0099CC'} onMouseLeave={e => e.currentTarget.style.color = '#374151'}>{office.email}</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team gallery */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px' }}>
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '8px', letterSpacing: '-0.5px' }}>Meet our team</h2>
              <p style={{ fontSize: '16px', color: '#6B7280' }}>We believe everyone has value to offer and we encourage each other to share our talents and ideas.</p>
            </div>
            <button
              onClick={() => navigate('/jobs')}
              style={{ backgroundColor: '#F97316', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600', whiteSpace: 'nowrap', transition: 'background-color 0.2s ease', display: 'inline-block' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EA580C'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F97316'}
            >
              Explore career
            </button>
          </div>

          {/* Gallery grid */}
          <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            {galleryImages.map((img, i) => (
              <div
                key={i}
                onClick={() => openLightbox(img)}
                style={{ borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', lineHeight: 0, aspectRatio: '1', position: 'relative' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                <img
                  src={img}
                  alt={`Team ${i + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
                  onError={e => { e.target.src = `https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=300&q=60&sig=${i}`; }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          onClick={() => setLightboxOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
        >
          <img src={lightboxImg} alt="Gallery" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }} />
          <button
            onClick={() => setLightboxOpen(false)}
            style={{ position: 'absolute', top: '24px', right: '24px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ✕
          </button>
        </div>
      )}
    </PageLayout>
  );
}
